import React, { useState, useEffect } from "react";
import cn from "classnames";

// types
import { Generic } from "types/filters-store.types";

// component
import { Icon } from "@trussworks/react-uswds";
import {
  Dialog,
  DialogContent,
  DialogHeading,
  DialogClose,
  IconButton,
  Button,
  ComboBox,
  MultiSelectCheckbox,
  Checkbox,
} from "components/LibraryComponents/Components";

// utils
import {
  getStateAbbreviation,
  formattedStatesListWithoutAbbreviation as StateList,
} from "utils/advanced-search";

// mock data
import { MockCongressionalDistricts } from "data/congressional-districts";

// constants
import { FilterKeys } from "constants/filters";

// store
import useFiltersStore from "store/useFiltersStoreNew";

import styles from "./CongressionalDialog.module.scss";

// query
import useZipCodes, { FilterTerm } from "../query";

type SelectedDistrics = { checked: boolean } & Generic;

type CongressionalDialogProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function CongressionalDialog(props: CongressionalDialogProps) {
  const { location, setFilter } = useFiltersStore((state) => ({
    location: state.filters.location,
    setFilter: state.setFilter,
  }));

  const { counties: stateCounties } = useZipCodes();

  const [currentState, setCurrentState] = useState<string>("");
  const [districts, setDistricts] = useState<Generic[]>([]);
  const [counties, setCounties] = useState<Generic[]>([]);
  const [isDistrictOpen, setIsDistrictOpen] = useState<boolean>(false);
  const [isCountiesOpen, setIsCountiesOpen] = useState<boolean>(false);
  const [selectedDistricts, setSelectedDistricts] = useState<
    SelectedDistrics[]
  >([]);
  const [selectedCounties, setSelectedCounties] = useState<SelectedDistrics[]>(
    []
  );

  const congressionalDistricts = filterCongressionalDistricts(currentState);
  const filteredCounties = filterCountries(stateCounties, currentState);
  const showSelected =
    currentState === "" &&
    (selectedDistricts.length > 0 || selectedCounties.length > 0);
  const isDisabled =
    selectedDistricts.filter((item) => item.checked).length === 0 &&
    selectedCounties.filter((item) => item.checked).length === 0;

  useEffect(() => {
    if (location.districts && location.districts.length > 0) {
      setDistricts(location.districts);
      setSelectedDistricts(
        location.districts.map((item) => ({ ...item, checked: true }))
      );
    }
  }, [location.districts]);

  useEffect(() => {
    if (location.counties && location.counties.length > 0) {
      setCounties(location.counties);
      setSelectedCounties(
        location.counties.map((item) => ({ ...item, checked: true }))
      );
    }
  }, [location.districts]);

  const handleStateChange = (value: string) => {
    if (!value) {
      setCurrentState("");
      return;
    }

    const stateName = value.split(" - ")[1];
    setCurrentState(stateName);
  };

  const handleChange = (items: Generic[]) => {
    setDistricts(items);

    setSelectedDistricts(items.map((item) => ({ ...item, checked: true })));
  };

  const handleCountiesChange = (items: FilterTerm[]) => {
    const newItems = items.map((item) => {
      if (item?.originalLabel) {
        // Value contins {county_name}-{state_name}-{fips_code}
        return { label: item.originalLabel, value: item.value };
      }

      return item;
    });

    setCounties(newItems);

    setSelectedCounties(newItems.map((item) => ({ ...item, checked: true })));
  };

  const handleSelectedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    // update selected distrcit checked value
    const newList = selectedDistricts.map((item) => {
      if (item.value === name) {
        return { ...item, checked };
      }
      return item;
    });

    setSelectedDistricts(newList);

    // filter out unchecked items and map them to districts state
    const filteredList = newList.filter((item) => item.checked);
    setDistricts(
      filteredList.map((item) => ({ value: item.value, label: item.label }))
    );
  };

  const handleCountiesSelectedChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = e.target;

    // update selected distrcit checked value
    const newList = selectedCounties.map((item) => {
      if (item.value === name) {
        return { ...item, checked };
      }
      return item;
    });

    setSelectedCounties(newList);

    // filter out unchecked items and map them to districts state
    const filteredList = newList.filter((item) => item.checked);
    setCounties(
      filteredList.map((item) => ({ value: item.value, label: item.label }))
    );
  };

  const handleApplyFilter = () => {
    // filter out unchecked items and map them to districts state
    const filteredList = selectedDistricts.filter((item) => item.checked);
    const districts = filteredList.map((item) => ({
      value: item.value,
      label: item.label,
    }));

    // filter out unchecked items and map them to counties state
    const filterCounties = selectedCounties.filter((item) => item.checked);
    const counties = filterCounties.map((item) => ({
      value: item.value,
      label: item.label,
    }));

    setFilter(FilterKeys.LOCATION, {
      ...location,
      districts,
      counties,
    });

    props.setOpen(false);
  };

  return (
    <Dialog open={props.open} onOpenChange={props.setOpen}>
      <DialogContent
        className={styles["congressional-dialog-root"]}
        hasBackdrop
      >
        <div className={styles["congressional-dialog-body"]}>
          <div className={styles["header"]}>
            <DialogHeading className={styles["heading"]}>
              Congressional districts and county code list
            </DialogHeading>
            <IconButton
              type="button"
              Icon={<Icon.Close aria-hidden="true" />}
              size="sm"
              onClick={() => props.setOpen(false)}
              tabIndex={-1}
              aria-label="Close dialog"
            />
          </div>

          <div className={styles["congressional-content"]}>
            <p className={styles["desc"]}>
              Choose a state or territory to see lists of congressional
              districts and counties or parishes by code. These congressional
              districts are based on the result of the 2020 census. They are set
              to be in effect from 2023–2032.
            </p>

            <div className={styles["combo-box-wrapper"]}>
              <ComboBox
                id="state-list"
                name="state-list"
                label="Select a state"
                options={StateList}
                onChange={handleStateChange}
              />
            </div>

            <div className={styles["list-content"]}>
              {/* show selected districts */}
              {showSelected && (
                <>
                  <p className={styles["selected-label"]}>SELECTED</p>

                  <div className={cn(styles["selected-list"], styles["mt-4"])}>
                    {selectedDistricts.map((item) => (
                      <li key={item.value} className={styles["item"]}>
                        <Checkbox
                          id={item.value}
                          name={item.value}
                          label={item.label}
                          onChange={handleSelectedChange}
                          checked={item.checked}
                        />
                      </li>
                    ))}
                  </div>

                  {/* show selected counties */}
                  <div className={cn(styles["selected-list"], styles["mt-2"])}>
                    {selectedCounties.map((item) => (
                      <li key={item.value} className={styles["item"]}>
                        <Checkbox
                          id={item.value}
                          name={item.value}
                          label={formatCountyLabel(item.value)}
                          onChange={handleCountiesSelectedChange}
                          checked={item.checked}
                          isInnerHtml
                        />
                      </li>
                    ))}
                  </div>
                </>
              )}

              {/* Congressional districts */}
              {/* Show list of districts by state */}
              {congressionalDistricts.length > 0 && (
                <div className={styles["accordion"]}>
                  <div
                    className={styles["accordion-head"]}
                    onClick={() => setIsDistrictOpen(!isDistrictOpen)}
                  >
                    <p className={styles["heading"]}>Congressional districts</p>
                    <Icon.ArrowDropDown
                      className={cn(styles["icon"], {
                        [styles["open"]]: isDistrictOpen,
                      })}
                      aria-label="accordion arrow"
                    />
                  </div>

                  {isDistrictOpen && (
                    <div className={styles["accordion-body"]}>
                      <div className={styles["list"]}>
                        <MultiSelectCheckbox
                          className={styles["list-wrapper"]}
                          items={congressionalDistricts}
                          checkedItems={districts}
                          handleChange={handleChange}
                          isInnerHtml
                          hideOnly
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Counties */}
              {/* Show list of counties by state */}
              {filteredCounties.length > 0 && (
                <div className={cn(styles["accordion"], styles["mt-4"])}>
                  <div
                    className={styles["accordion-head"]}
                    onClick={() => setIsCountiesOpen(!isCountiesOpen)}
                  >
                    <p className={styles["heading"]}>Counties by code</p>
                    <Icon.ArrowDropDown
                      className={cn(styles["icon"], {
                        [styles["open"]]: isCountiesOpen,
                      })}
                      aria-label="accordion arrow"
                    />
                  </div>

                  {isCountiesOpen && (
                    <div className={styles["accordion-body"]}>
                      <div className={styles["list"]}>
                        <MultiSelectCheckbox
                          className={styles["list-wrapper"]}
                          items={filteredCounties}
                          checkedItems={counties}
                          handleChange={handleCountiesChange}
                          isInnerHtml
                          hideOnly
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles["congressional-dialog-footer"]}>
          <DialogClose
            className={styles["btn-close"]}
            aria-label="Close dialog"
          >
            Cancel and close
          </DialogClose>

          <Button
            type="submit"
            className={styles["btn-submit"]}
            aria-label="Submit filter"
            disabled={isDisabled}
            onClick={handleApplyFilter}
          >
            Apply filter(s)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const filterCongressionalDistricts = (state: string) => {
  if (!state) {
    return [];
  }

  const districts = MockCongressionalDistricts.filter(
    (district) => district.state === state
  );

  return districts.map((district) => ({
    label: `${district.state}-${district.fips}`,
    value: `${district.state}-${district.fips}`,
  }));
};

const filterCountries = (counties: FilterTerm[], state: string) => {
  if (!state) {
    return [];
  }

  const stateCounties = counties.filter((c) => c.stateName === state);

  // sort counties by label
  const sortedCounties = stateCounties.sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  return sortedCounties.map((item) => {
    return {
      ...item,
      label: formatCountyLabel(item.value),
    };
  });
};

const formatCountyLabel = (value: string) => {
  const [county, state, fipCode] = value.split("-");

  const stateAbbr = getStateAbbreviation(state);
  const code = fipCode.slice(2);

  return `<div><span style="font-weight: 400;">${code}</span><span>${county}, ${stateAbbr}</span></div>`;
};
